/**
 * `availability-save-range` — the "Add availability" Save step's slot-generation
 * plan and its verdict.
 *
 * Save writes the parsed windows with `set_trainer_availability`, then
 * materialises bookable `slots` with `generate_slots`. Until 2026-09-02 that
 * second call ran over a fixed `today → today + 60 days` no matter what dates the
 * windows carried, so a bounded block starting later than that (a January rota
 * pasted in September) saved its windows, generated NOTHING, and the modal still
 * said "Saved" — the operator pasted it again and doubled the windows.
 *
 * The rule now:
 *  - a BOUNDED window generates for ITS OWN dates — clamped to today (no past
 *    slots), merged with overlapping siblings, and chunked under the canonical
 *    action's span cap;
 *  - only a PERMANENT window (no end) gets a horizon, and the nightly
 *    `native_availability_roller` keeps extending that;
 *  - zero slots for windows that have dates is a FAILURE, never a "Saved".
 *
 * Pure `YYYY-MM-DD` string arithmetic in UTC: no clock and no timezone in here —
 * the caller passes the studio's `today`.
 */

/**
 * How far ahead a PERMANENT window is seeded at Save time. It has no end, so
 * some horizon is unavoidable; the roller tops it up every night.
 */
export const PERMANENT_WINDOW_HORIZON_DAYS = 60;

/**
 * Mirror of `GENERATE_SLOTS_MAX_SPAN_DAYS` in `@layer/canonical-actions`. The
 * SPA bundle cannot import the server package, so the value is repeated here and
 * pinned equal by `availability-save-range.test.ts` (A8).
 */
export const GENERATE_SPAN_CAP_DAYS = 90;

export interface WindowBounds {
  readonly effective_from: string | null;
  readonly effective_until: string | null;
}

export interface DateRange {
  readonly from_date: string;
  readonly to_date: string;
}

/** The counts one `generate_slots` call returns. */
export interface GenerationTotals {
  readonly slots_created: number;
  readonly slots_skipped: number;
  readonly windows_expanded: number;
}

export type SaveVerdict =
  | { readonly ok: true; readonly note: string | null }
  | { readonly ok: false; readonly reason: string };

const DAY_MS = 86_400_000;

function toMs(key: string): number {
  return Date.parse(`${key}T00:00:00Z`);
}

function keyOf(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** `YYYY-MM-DD` + `n` days, in UTC. */
export function addDaysKey(key: string, n: number): string {
  return keyOf(toMs(key) + n * DAY_MS);
}

/**
 * The `generate_slots` ranges that cover every date the given windows can
 * produce a slot on, from `today` onwards. Ascending, non-overlapping, each
 * within the action's span cap.
 */
export function planGenerateRanges(
  windows: ReadonlyArray<WindowBounds>,
  today: string,
): ReadonlyArray<DateRange> {
  const raw: DateRange[] = [];
  for (const w of windows) {
    const start =
      w.effective_from !== null && w.effective_from > today
        ? w.effective_from
        : today;
    const end =
      w.effective_until ?? addDaysKey(start, PERMANENT_WINDOW_HORIZON_DAYS);
    if (end < start) continue; // wholly in the past — nothing to generate
    raw.push({ from_date: start, to_date: end });
  }

  raw.sort((a, b) => (a.from_date < b.from_date ? -1 : a.from_date > b.from_date ? 1 : 0));
  const merged: DateRange[] = [];
  for (const r of raw) {
    const last = merged[merged.length - 1];
    if (last !== undefined && toMs(r.from_date) <= toMs(last.to_date) + DAY_MS) {
      if (r.to_date > last.to_date) {
        merged[merged.length - 1] = { from_date: last.from_date, to_date: r.to_date };
      }
    } else {
      merged.push(r);
    }
  }

  const out: DateRange[] = [];
  for (const block of merged) {
    let from = block.from_date;
    while (from <= block.to_date) {
      const capEnd = addDaysKey(from, GENERATE_SPAN_CAP_DAYS);
      const to = capEnd < block.to_date ? capEnd : block.to_date;
      out.push({ from_date: from, to_date: to });
      from = addDaysKey(to, 1);
    }
  }
  return out;
}

export function sumGeneration(
  parts: ReadonlyArray<GenerationTotals>,
): GenerationTotals {
  return parts.reduce(
    (acc, p) => ({
      slots_created: acc.slots_created + p.slots_created,
      slots_skipped: acc.slots_skipped + p.slots_skipped,
      windows_expanded: acc.windows_expanded + p.windows_expanded,
    }),
    { slots_created: 0, slots_skipped: 0, windows_expanded: 0 },
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function humanDate(key: string): string {
  const d = new Date(toMs(key));
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()] ?? "?"} ${d.getUTCFullYear()}`;
}

/**
 * The dates a plan covers, for the operator: contiguous chunks read as one
 * block (`4 Jan 2027 – 31 Jan 2027`), separate blocks are joined with "and".
 */
export function describeCoverage(ranges: ReadonlyArray<DateRange>): string {
  if (ranges.length === 0) return "no dates";
  const blocks: DateRange[] = [];
  for (const r of ranges) {
    const last = blocks[blocks.length - 1];
    if (last !== undefined && addDaysKey(last.to_date, 1) === r.from_date) {
      blocks[blocks.length - 1] = { from_date: last.from_date, to_date: r.to_date };
    } else {
      blocks.push(r);
    }
  }
  return blocks
    .map((b) => `${humanDate(b.from_date)} – ${humanDate(b.to_date)}`)
    .join(" and ");
}

/**
 * Did the Save do what the operator asked — bookable slots on the calendar?
 * Windows landing is necessary, not sufficient.
 */
export function judgeSaveOutcome(args: {
  readonly windows_set: number;
  readonly ranges: ReadonlyArray<DateRange>;
  readonly totals: GenerationTotals;
}): SaveVerdict {
  const { windows_set, ranges, totals } = args;
  if (windows_set === 0) {
    return { ok: false, reason: "No windows were saved." };
  }
  if (ranges.length === 0) {
    return {
      ok: false,
      reason: `${windows_set} window${windows_set === 1 ? "" : "s"} saved, but every date is already in the past — no slots to generate.`,
    };
  }
  const touched = totals.slots_created + totals.slots_skipped;
  if (touched === 0) {
    return {
      ok: false,
      reason: `${windows_set} window${windows_set === 1 ? "" : "s"} saved, but 0 slots came out for ${describeCoverage(ranges)} — nothing is bookable. The windows are stored, so don't paste them again: check the trainer's schedule on the calendar, or ask for help.`,
    };
  }
  if (totals.slots_created === 0) {
    // No count here: `slots_skipped` counts candidate slots per window, so a
    // repeat paste that APPENDED duplicate windows reports double the real number.
    return { ok: true, note: "those slots already existed — nothing new to add" };
  }
  return { ok: true, note: null };
}

/**
 * The failure line when one or more `generate_slots` chunks were rejected. Every
 * chunk is attempted (a failure on one must not silently skip the rest), so the
 * line names BOTH what landed and exactly which dates have no slots — the
 * operator must never read an unmentioned block as fine.
 */
export function describeChunkFailure(args: {
  readonly windows_set: number;
  readonly done: ReadonlyArray<DateRange>;
  readonly failed: ReadonlyArray<{ readonly range: DateRange; readonly message: string }>;
  readonly totals: GenerationTotals;
}): string {
  const { windows_set, done, failed, totals } = args;
  const saved = `${windows_set} window${windows_set === 1 ? "" : "s"} saved`;
  const landed =
    done.length > 0
      ? `${totals.slots_created} slots generated for ${describeCoverage(done)}, but `
      : "";
  const firstMessage = failed[0]?.message ?? "unknown error";
  return `${saved}; ${landed}generating slots FAILED for ${describeCoverage(failed.map((f) => f.range))} (${firstMessage}) — those dates have no slots. The windows are stored, so don't paste them again: retry later, or ask for help.`;
}

// ---------------------------------------------------------------------------
// 2026-09-02 · dated pastes go through ONE `upload_trainer_rota` (period form):
// windows replaced for the period, slots reconciled (stale unbooked ones
// pruned), booked slots outside the new schedule reported, never deleted.
// ---------------------------------------------------------------------------

/** The counts + report `upload_trainer_rota` returns. */
export interface RotaApplyResponse {
  readonly windows_set?: number;
  readonly slots_created?: number;
  readonly slots_skipped?: number;
  readonly slots_pruned?: number;
  readonly conflicts?: ReadonlyArray<{
    readonly starts_at: string;
    readonly taken_count: number;
  }>;
  readonly skipped_reason?: string | null;
}

/**
 * The period a fully-bounded paste covers: the earliest start to the latest
 * end across its windows. Throws on an unbounded window — the caller routes
 * those through the ongoing flow instead.
 */
export function rotaPeriodFor(windows: ReadonlyArray<WindowBounds>): DateRange {
  let from: string | null = null;
  let until: string | null = null;
  for (const w of windows) {
    if (w.effective_from === null || w.effective_until === null) {
      throw new Error("rotaPeriodFor: every window must be bounded");
    }
    if (from === null || w.effective_from < from) from = w.effective_from;
    if (until === null || w.effective_until > until) until = w.effective_until;
  }
  if (from === null || until === null) {
    throw new Error("rotaPeriodFor: no windows");
  }
  return { from_date: from, to_date: until };
}

function humanInstant(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${humanDate(d.toISOString().slice(0, 10))} ${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")} UTC`;
}

/**
 * Did the reconcile do what the operator asked — the pasted windows are now
 * the trainer's schedule for the period, with bookable slots? Anything the
 * reconcile removed or could not remove is said out loud.
 */
export function judgeRotaOutcome(args: {
  readonly period: DateRange;
  readonly response: RotaApplyResponse | undefined;
}): SaveVerdict {
  const r = args.response ?? {};
  const skipped = r.skipped_reason ?? null;
  if (skipped !== null) {
    return {
      ok: false,
      reason: `Nothing was written: the studio's availability set-up refused it (${skipped}). Ask for help.`,
    };
  }
  const windowsSet = r.windows_set ?? 0;
  const created = r.slots_created ?? 0;
  const kept = r.slots_skipped ?? 0;
  const pruned = r.slots_pruned ?? 0;
  const conflicts = r.conflicts ?? [];
  if (windowsSet === 0) {
    return { ok: false, reason: "No windows were saved." };
  }
  if (created + kept === 0) {
    return {
      ok: false,
      reason: `${windowsSet} window${windowsSet === 1 ? "" : "s"} saved, but 0 slots came out for ${describeCoverage([args.period])} — nothing is bookable. The windows are stored, so don't paste them again: check the trainer's schedule on the calendar, or ask for help.`,
    };
  }
  const notes: string[] = [];
  if (created === 0) notes.push("those slots already existed — nothing new to add");
  if (pruned > 0) notes.push(`${pruned} old slot${pruned === 1 ? "" : "s"} that no longer fit removed`);
  if (conflicts.length > 0) {
    const first = conflicts
      .slice(0, 3)
      .map((c) => `${humanInstant(c.starts_at)} (${c.taken_count} booked)`)
      .join(", ");
    notes.push(
      `${conflicts.length} booked slot${conflicts.length === 1 ? "" : "s"} outside this schedule kept, not deleted: ${first}${conflicts.length > 3 ? ", …" : ""} — move or cancel those bookings by hand`,
    );
  }
  return { ok: true, note: notes.length > 0 ? notes.join("; ") : null };
}
